import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, UserPlus, Clock } from 'lucide-react';
import axios from 'axios';
import CustomerFormDialog from '@/components/CustomerFormDialog';

export default function Sales() {
    const { API } = useContext(AuthContext);
    const [sales, setSales] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

    // Invoice Form
    const [form, setForm] = useState({
        rut_cliente: '',
        razon_social: '',
        folio: '',
        fecha_emision: new Date().toISOString().split('T')[0],
        monto_total: '',
        tipo_documento: 'Factura Electronica',
        payment_terms: '30 dias'
    });

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load independently so one failure doesn't break the other
            const customersRes = await axios.get(`${API}/customers`).catch(() => ({ data: [] }));
            const salesRes = await axios.get(`${API}/sales/unmatched`).catch(() => ({ data: [] }));

            setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
            setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
        } catch (error) {
            console.error(error);
            toast.error("Error cargando datos");
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSelect = (rut) => {
        const customer = customers.find(c => c.rut === rut);
        if (customer) {
            setForm({
                ...form,
                rut_cliente: customer.rut,
                razon_social: customer.name
            });
        }
    };

    const handleCustomerCreated = (newCustomer) => {
        // Add to customers list
        setCustomers([...customers, newCustomer]);
        // Auto-select the new customer
        setForm({
            ...form,
            rut_cliente: newCustomer.rut,
            razon_social: newCustomer.name
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const total = parseInt(form.monto_total);
            const neto = Math.round(total / 1.19);
            const iva = total - neto;

            const entry = {
                ...form,
                folio: parseInt(form.folio),
                monto_total: total,
                monto_neto: neto,
                monto_iva: iva,
                status: 'pending'
            };

            await axios.post(`${API}/sales/upload`, [entry]);
            toast.success("Venta registrada exitosamente");
            loadData(); // Reload sales
            setForm({
                rut_cliente: '',
                razon_social: '',
                folio: '',
                fecha_emision: new Date().toISOString().split('T')[0],
                monto_total: '',
                tipo_documento: 'Factura Electronica',
                payment_terms: '30 dias'
            });
        } catch (error) {
            toast.error("Error al registrar venta");
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900">Ventas</h1>
                    <p className="text-slate-500 mt-2">Registro de Facturas Emitidas (Libro de Ventas)</p>
                </div>
            </div>

            {/* Customer Form Dialog - Shared Component */}
            <CustomerFormDialog
                open={isCustomerModalOpen}
                onOpenChange={setIsCustomerModalOpen}
                onSuccess={handleCustomerCreated}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Formulario de Ingreso */}
                <Card className="lg:col-span-1 border-slate-200 shadow-sm h-fit">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Plus className="h-5 w-5 text-indigo-600" />
                            Nueva Venta
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">

                            {/* Customer Selection Section */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <Label>Cliente</Label>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-xs text-indigo-600 hover:text-indigo-700 p-0"
                                        onClick={() => setIsCustomerModalOpen(true)}
                                    >
                                        <UserPlus className="h-3 w-3 mr-1" />
                                        Nuevo Cliente
                                    </Button>
                                </div>

                                <Select onValueChange={handleCustomerSelect} value={form.rut_cliente}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Seleccionar Cliente..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.isArray(customers) && customers.map(c => (
                                            <SelectItem key={c.rut} value={c.rut}>
                                                {c.name} {c.rut ? `(${c.rut})` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>RUT (Auto)</Label>
                                <Input disabled value={form.rut_cliente} placeholder="Seleccione cliente..." />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Folio</Label>
                                    <Input
                                        type="number"
                                        placeholder="123"
                                        value={form.folio}
                                        onChange={(e) => setForm({ ...form, folio: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Fecha</Label>
                                    <Input
                                        type="date"
                                        value={form.fecha_emision}
                                        onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Monto Total ($)</Label>
                                <Input
                                    type="number"
                                    placeholder="100000"
                                    value={form.monto_total}
                                    onChange={(e) => setForm({ ...form, monto_total: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Condición de Pago</Label>
                                <Select
                                    value={form.payment_terms}
                                    onValueChange={(v) => setForm({ ...form, payment_terms: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Contado">Contado</SelectItem>
                                        <SelectItem value="30 dias">Crédito 30 días</SelectItem>
                                        <SelectItem value="60 dias">Crédito 60 días</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                                Registrar Factura
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Listado de Ventas Pendientes */}
                <Card className="lg:col-span-2 border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-5 w-5 text-amber-500" />
                            Facturas Pendientes de Pago (Por Cobrar)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="rounded-md border border-slate-200">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Fecha</TableHead>
                                        <TableHead>Folio</TableHead>
                                        <TableHead>Cliente</TableHead>
                                        <TableHead>Total</TableHead>
                                        <TableHead>Estado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sales.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                                No hay ventas pendientes de cobro
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sales.map((sale) => (
                                            <TableRow key={sale.id}>
                                                <TableCell>{sale.fecha_emision}</TableCell>
                                                <TableCell className="font-medium">{sale.folio}</TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-slate-900">{sale.razon_social}</div>
                                                    <div className="text-xs text-slate-500">{sale.rut_cliente}</div>
                                                </TableCell>
                                                <TableCell className="font-semibold text-slate-700">
                                                    {formatCurrency(sale.monto_total)}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                        Pendiente
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
